import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table"
import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useAtom } from "jotai"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import api from "../../services/api"
import { StatusBadge } from "../../Components/Admin/StatusBadge"
import { InvitationStatusBadge } from "../../Components/Admin/InvitationStatusBadge"
import { isSuperAdminAtom } from "../../state"
import AdminLayout from "../../Components/Layout/AdminLayout"
import Button from "../../Components/Button"
import { PLAN_STATUS } from "../../constants/planStatus"
import { INVITE_STATUS } from "../../constants/inviteStatus"
import Modal from "../../Components/Modal"
import EditPlan from "../../Partials/EditPlan"
import ArchivePlanModal from "../../Components/Modals/ArchivePlanModal"
import ChangePlanOrganizationModal from "../../Components/Modals/ChangePlanOrganizationModal"

const col = createColumnHelper()

const PlanActionsMenu = ({
  plan,
  onEditName,
  onEdit,
  onView,
  onArchive,
  onChangeOrg,
  onInviteMentors,
  isSuperAdmin,
}) => {
  if (!plan) return null
  return (
    <Menu as="div" className="relative flex h-full items-center">
      <MenuButton className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand">
        Actions
        <i className="fa-solid fa-chevron-down text-[10px] text-gray-500" />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 w-44 rounded-md bg-white text-sm shadow-lg ring-1 ring-gray-200 focus:outline-none"
      >
        <div className="py-1">
          <MenuItem>
            <button onClick={onEditName} className="flex w-full items-center gap-2 px-3 py-2 text-gray-700 data-[focus]:bg-gray-50">
              <i className="fa-solid fa-pen-to-square w-4 text-gray-400" />
              Edit Plan Name
            </button>
          </MenuItem>
          <MenuItem>
            <button onClick={onEdit} className="flex w-full items-center gap-2 px-3 py-2 text-gray-700 data-[focus]:bg-gray-50">
              <i className="fa-solid fa-pen w-4 text-gray-400" />
              Edit Plan
            </button>
          </MenuItem>
          <MenuItem>
            <button onClick={onView} className="flex w-full items-center gap-2 px-3 py-2 text-gray-700 data-[focus]:bg-gray-50">
              <i className="fa-solid fa-arrow-up-right-from-square w-4 text-gray-400" />
              View Details
            </button>
          </MenuItem>
          {(plan.status === PLAN_STATUS.INVITE_MENTORS || plan.status === PLAN_STATUS.SEND_PLAN) &&
          plan.mentors?.some(m =>
            m.pivot?.invitation_status !== INVITE_STATUS.ACCEPTED &&
            m.pivot?.invitation_status !== INVITE_STATUS.DECLINED,
          ) ? (
            <MenuItem>
              <button onClick={onInviteMentors} className="flex w-full items-center gap-2 px-3 py-2 text-green-700 data-[focus]:bg-green-50">
                <i className="fa-solid fa-envelope w-4 text-green-500" />
                Invite Mentors
              </button>
            </MenuItem>
          ) : null}
          <MenuItem>
            <button onClick={onArchive} className="flex w-full items-center gap-2 px-3 py-2 text-red-600 data-[focus]:bg-red-50">
              <i className="fa-solid fa-box-archive w-4 text-red-400" />
              Delete
            </button>
          </MenuItem>
          {isSuperAdmin ? (
            <MenuItem>
              <button onClick={onChangeOrg} className="flex w-full items-center gap-2 px-3 py-2 text-circle data-[focus]:bg-circle/10">
                <i className="fa-solid fa-building w-4 text-circle" />
                Change Org
              </button>
            </MenuItem>
          ) : null}
        </div>
      </MenuItems>
    </Menu>
  )
}

const SECTIONS = [
  { name: "Identity",      field: "identities" },
  { name: "Gifts",         field: "gifts" },
  { name: "Vision",        field: "visions" },
  { name: "Encouragements", field: "encouragements" },
  { name: "Commitments",   field: "commitments" },
]


const AdminPlans = () => {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sorting, setSorting] = useState([{ id: "last_activity", desc: true }])
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 })
  const [pageCount, setPageCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [roleFilter, setRoleFilter] = useState("")
  const [showColumnPanel, setShowColumnPanel] = useState(false)
  const [columnSizing, setColumnSizing] = useState({})
  const debounceRef = useRef(null)

  const navigate = useNavigate()
  const { organization_slug } = useParams()
  const [organization, setOrganization] = useState(null)
  const [isSuperAdmin] = useAtom(isSuperAdminAtom)

  const [selectedPlan, setSelectedPlan] = useState(null)
  const [editPlanOpen, setEditPlanOpen] = useState(false)
  const [archivePlanOpen, setArchivePlanOpen] = useState(false)
  const [changeOrgOpen, setChangeOrgOpen] = useState(false)
  const [inviteMentorsOpen, setInviteMentorsOpen] = useState(false)
  const [invitingMentors, setInvitingMentors] = useState(false)
  const [inviteMentorsError, setInviteMentorsError] = useState(null)
  const [inviteMentorsList, setInviteMentorsList] = useState([])
  const [inviteMentorsListLoading, setInviteMentorsListLoading] = useState(false)
  const [selectedMentorEmails, setSelectedMentorEmails] = useState(new Set())

  // Column visibility with localStorage persistence
  const STORAGE_KEY = "admin-plans-column-visibility"

  const [columnVisibility, setColumnVisibility] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved) : {}
    } catch {
      return {}
    }
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columnVisibility))
  }, [columnVisibility])

  // Debounce search
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText)
      setPagination(p => ({ ...p, pageIndex: 0 }))
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [searchText])

  // Load org once when the slug changes
  useEffect(() => {
    if (organization_slug) {
      api.getOrganizationBySlug(organization_slug)
        .then(res => setOrganization(res.data))
        .catch(() => { setOrganization(null); setPlans([]); setLoading(false) })
    } else {
      setOrganization(null)
    }
  }, [organization_slug])

  const fetchPlans = useCallback((orgId) => {
    setLoading(true)
    const params = { all: true, page: pagination.pageIndex + 1, per_page: pagination.pageSize }
    if (orgId) params.organization_id = orgId
    if (roleFilter) params.role = roleFilter
    if (debouncedSearch) params.search = debouncedSearch
    if (sorting.length > 0) {
      params.sort_by  = sorting[0].id
      params.sort_dir = sorting[0].desc ? "desc" : "asc"
    }

    api.getPlans(params)
      .then(res => {
        const data = res.data.data ?? res.data
        setPlans(data)
        setPageCount(res.data.last_page ?? 1)
        setTotal(res.data.total ?? data.length)
        setLoading(false)
      })
      .catch(() => { setPlans([]); setLoading(false) })
  }, [pagination.pageIndex, pagination.pageSize, roleFilter, debouncedSearch, sorting])

  // Fetch plans whenever pagination/search/role changes, or when org finishes loading
  useEffect(() => {
    if (organization_slug && !organization) return  // wait for org to load
    fetchPlans(organization?.id ?? null)
  }, [fetchPlans, organization, organization_slug])

  const refreshPlans = useCallback(() => {
    fetchPlans(organization?.id ?? null)
  }, [organization, fetchPlans])

  const handleOpenInviteMentors = useCallback((plan) => {
    setSelectedPlan(plan)
    setInviteMentorsOpen(true)
    setInviteMentorsList([])
    setInviteMentorsError(null)
    setInviteMentorsListLoading(true)
    api.getMentors(plan.token)
      .then(res => {
        const mentors = res.data?.mentors || []
        setInviteMentorsList(mentors)
        setSelectedMentorEmails(new Set(
          mentors
            .filter(m => m.invitation_status === INVITE_STATUS.NOT_INVITED || m.invitation_status === INVITE_STATUS.PENDING)
            .map(m => m.email)
        ))
      })
      .catch(() => setInviteMentorsError("Failed to load mentors."))
      .finally(() => setInviteMentorsListLoading(false))
  }, [])

  const toggleMentorEmail = useCallback((email) => {
    setSelectedMentorEmails(prev => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email)
      else next.add(email)
      return next
    })
  }, [])

  const handleInviteMentorsAction = useCallback(async () => {
    if (!selectedPlan) return
    setInvitingMentors(true)
    setInviteMentorsError(null)
    const mentorsToInvite = inviteMentorsList
      .filter(m => selectedMentorEmails.has(m.email))
      .map(m => ({ email: m.email, first_name: m.first_name, last_name: m.last_name }))
    if (mentorsToInvite.length === 0) {
      setInviteMentorsError("No mentors selected.")
      setInvitingMentors(false)
      return
    }
    try {
      await api.inviteMentors(selectedPlan.token, { mentors: mentorsToInvite })
      setInviteMentorsOpen(false)
      refreshPlans()
    } catch (error) {
      setInviteMentorsError(error.response?.data?.message || "Failed to send invitations.")
    } finally {
      setInvitingMentors(false)
    }
  }, [selectedPlan, inviteMentorsList, selectedMentorEmails, refreshPlans])

  const handleExportCsv = useCallback(() => {
    const exportParams = { all: true, export: 1 }
    if (organization?.id) exportParams.organization_id = organization.id
    if (roleFilter) exportParams.role = roleFilter
    if (debouncedSearch) exportParams.search = debouncedSearch

    api.getPlans(exportParams).then(res => {
      const all = res.data.data ?? res.data
      const headers = [
        "Plan Name", "Status", "Student Name", "Student Email",
        "Guide Name", "Guide Email", "Organization", "# Mentors", "Mentor Emails", "Guardian Emails",
        ...SECTIONS.map(s => s.name), "Blessings",
        "Student Activities Completed", "Mentor Activities Completed", "Plan Sent",
        "Created", "Last Activity",
      ]
      const rows = all.map(plan => {
        const youth = plan.youth?.[0]
        const guide = plan.guide?.[0]
        const mentorEmails = (plan.mentors || []).map(m => m.email).join("; ")
        const guardianEmails = (plan.guardians || []).map(g => g.email).join("; ")
        const sectionValues = SECTIONS.map(s =>
          plan.content?.filter(item => item.key === s.field).length > 0 ? "Yes" : ""
        )
        const hasTextBlessing = plan.content?.some(item => item.key === "text_blessings" && item.value)
        const hasAudioBlessing = plan.content?.some(item => item.key === "audio_blessings" && item.value)
        const studentActivities = plan.student_sections_completed_at
          ? new Date(plan.student_sections_completed_at).toLocaleDateString()
          : (plan.status_history?.find(h => h.from_status === PLAN_STATUS.SIT_WITH_STUDENT)
            ? new Date(plan.status_history.find(h => h.from_status === PLAN_STATUS.SIT_WITH_STUDENT).created_at).toLocaleDateString()
            : "")
        const mentorActivities = plan.mentor_sections_completed_at
          ? new Date(plan.mentor_sections_completed_at).toLocaleDateString()
          : ""
        const planSent = plan.status_history?.find(h => h.from_status === PLAN_STATUS.SEND_PLAN)
          ? new Date(plan.status_history.find(h => h.from_status === PLAN_STATUS.SEND_PLAN).created_at).toLocaleDateString()
          : ""

        return [
          `"${(plan.name || "Untitled Plan").replace(/"/g, '""')}"`,
          plan.status || "",
          youth?.name || "",
          youth?.email || "",
          guide?.name || "",
          guide?.email || "",
          plan.organization?.name || plan.created_by?.organizations?.[0]?.name || "",
          plan.mentors?.length || 0,
          `"${mentorEmails}"`,
          `"${guardianEmails}"`,
          ...sectionValues,
          (hasTextBlessing || hasAudioBlessing) ? "Yes" : "",
          studentActivities,
          mentorActivities,
          planSent,
          plan.created_at ? new Date(plan.created_at).toLocaleDateString() : "",
          plan.last_activity ? new Date(plan.last_activity).toLocaleDateString() : "",
        ]
      })

      const csv = [headers, ...rows].map(r => r.join(",")).join("\n")
      const blob = new Blob([csv], { type: "text/csv" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      const orgSlug = organization?.slug || "all"
      a.download = `encircled-plans-${orgSlug}-${new Date().toISOString().split("T")[0]}.csv`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 100)
    })
  }, [organization, roleFilter, debouncedSearch])

  const columns = useMemo(() => [
    col.accessor("name", {
      header: "Plan Name",
      size: 280,
      minSize: 200,
      cell: ({ row }) => (
        <Link to={`/admin/plans/${row.original.token}`} className="underline text-gray-700 hover:text-brandSecondary text-sm">
          {row.original.name || "Untitled Plan"}
        </Link>
      ),
    }),
    col.accessor("status", {
      header: "Status",
      size: 150,
      minSize: 120,
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    }),
    col.accessor(row => row.youth?.[0]?.name ?? "", {
      id: "youth_name",
      header: "Student Name",
      size: 140,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => row.youth?.[0]?.email ?? "", {
      id: "youth_email",
      header: "Student Email",
      size: 180,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => row.guide?.[0]?.name ?? "", {
      id: "guide_name",
      header: "Guide Name",
      size: 140,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => row.guide?.[0]?.email ?? "", {
      id: "guide_email",
      header: "Guide Email",
      size: 180,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => row.organization?.name || row.created_by?.organizations?.map(o => o.name).join(', ') || "", {
      id: "organization",
      header: "Organization",
      size: 140,
      minSize: 70,
      enableSorting: true,
    }),
    col.accessor(row => row.mentors?.length ?? 0, {
      id: "mentors",
      header: "# Mentors",
      size: 90,
      minSize: 70,
      enableSorting: false,
      cell: ({ getValue }) => {
        const n = getValue()
        return n > 0 ? <span>{n} ✅</span> : <span className="text-gray-400">0</span>
      },
    }),
    col.accessor(row => (row.mentors || []).map(m => m.email).join("; "), {
      id: "mentor_emails",
      header: "Mentor Emails",
      size: 250,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => (row.guardians || []).map(g => g.email).join("; "), {
      id: "guardian_emails",
      header: "Guardian Emails",
      size: 250,
      minSize: 70,
      enableSorting: false,
    }),
    ...SECTIONS.map(section =>
      col.accessor(
        row => row.content?.filter(item => item.key === section.field).length > 0 ? "✅" : "",
        {
          id: section.field,
          header: section.name,
          size: 100,
          minSize: 70,
          enableSorting: false,
        }
      )
    ),
    col.accessor(row => {
      const hasText = row.content?.some(item => item.key === "text_blessings" && item.value)
      const hasAudio = row.content?.some(item => item.key === "audio_blessings" && item.value)
      return hasText || hasAudio ? "✅" : ""
    }, {
      id: "blessings",
      header: "Blessings",
      size: 100,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => {
      if (row.student_sections_completed_at) return new Date(row.student_sections_completed_at).toLocaleDateString()
      const h = row.status_history?.find(item => item.from_status === PLAN_STATUS.SIT_WITH_STUDENT)
      return h ? new Date(h.created_at).toLocaleDateString() : ""
    }, {
      id: "student_activities_completed",
      header: "Student Activities Completed",
      size: 120,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => {
      if (row.mentor_sections_completed_at) return new Date(row.mentor_sections_completed_at).toLocaleDateString()
      return ""
    }, {
      id: "mentor_activities_completed",
      header: "Mentor Activities Completed",
      size: 120,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => {
      const h = row.status_history?.find(item => item.from_status === PLAN_STATUS.SEND_PLAN)
      return h ? new Date(h.created_at).toLocaleDateString() : ""
    }, {
      id: "plan_sent",
      header: "Plan Sent",
      size: 100,
      minSize: 70,
      enableSorting: false,
    }),
    col.accessor(row => row.created_at ? new Date(row.created_at).toLocaleDateString() : "", {
      id: "created_at",
      header: "Created",
      size: 120,
      minSize: 70,
    }),
    col.accessor(row => row.last_activity ? new Date(row.last_activity).toLocaleDateString() : "", {
      id: "last_activity",
      header: "Last Activity",
      size: 120,
      minSize: 70,
    }),
    col.display({
      id: "actions",
      header: "Actions",
      size: 110,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => (
        <PlanActionsMenu
          plan={row.original}
          isSuperAdmin={isSuperAdmin}
          onEditName={() => { setSelectedPlan(row.original); setEditPlanOpen(true) }}
          onEdit={() => navigate(`/plan/${row.original.token}/activities`)}
          onView={() => navigate(`/admin/plans/${row.original.token}`)}
          onArchive={() => { setSelectedPlan(row.original); setArchivePlanOpen(true) }}
          onChangeOrg={() => { setSelectedPlan(row.original); setChangeOrgOpen(true) }}
          onInviteMentors={() => handleOpenInviteMentors(row.original)}
        />
      ),
    }),
  ], [isSuperAdmin, navigate, handleOpenInviteMentors])

  const table = useReactTable({
    data: plans,
    columns,
    pageCount,
    columnResizeMode: "onChange",
    state: { sorting, pagination, columnVisibility, columnSizing },
    onSortingChange: updater => {
      setSorting(updater)
      setPagination(p => ({ ...p, pageIndex: 0 }))
    },
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  const { pageIndex, pageSize } = pagination
  const from = total === 0 ? 0 : pageIndex * pageSize + 1
  const to = Math.min((pageIndex + 1) * pageSize, total)

  return (
    <AdminLayout>
      <div className="p-8 flex flex-col h-full">
        <div className="mb-6">
          <h1 className="font-bold text-3xl text-brandSecondary">
            {organization ? organization.name : "Plan Management"}
          </h1>
          <p className="text-gray-600 mt-2">
            {organization ? `Plans for ${organization.name}` : "View and manage all plans in the system"}
          </p>

          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <div className="flex-1 min-w-48 max-w-md">
              <input
                type="text"
                placeholder="Search plans..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent text-sm"
              />
            </div>
            <select
              value={roleFilter}
              onChange={e => { setRoleFilter(e.target.value); setPagination(p => ({ ...p, pageIndex: 0 })) }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand bg-white"
            >
              <option value="">All Plans</option>
              <option value="guide">My Role: Guide</option>
              <option value="youth">My Role: Student</option>
              <option value="mentor">My Role: Mentor</option>
            </select>
            <span className="text-sm text-gray-500">{total} plan{total !== 1 ? "s" : ""}</span>

            <div className="flex-grow" />

            {/* Column visibility panel */}
            <div className="relative">
              <Button
                variant="secondary-outline"
                onClick={() => setShowColumnPanel(v => !v)}
                rounded="rounded-lg"
                padding="px-2 py-2"
                className="text-sm"
              >
                <i className="fa-solid fa-table-columns mr-2" />
                Show/Hide Columns
              </Button>
              {showColumnPanel && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowColumnPanel(false)} />
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
                    <div className="p-3 border-b border-gray-200 font-semibold text-sm bg-gray-50 flex items-center justify-between">
                      <span>Column Visibility</span>
                      <button
                        onClick={() => setColumnVisibility({})}
                        className="text-xs text-brand hover:underline"
                      >
                        Reset
                      </button>
                    </div>
                    <div className="p-2">
                      {table.getAllColumns().filter(c => c.getCanHide()).map(column => (
                        <label key={column.id} className="flex items-center px-2 py-2 hover:bg-gray-50 cursor-pointer rounded">
                          <input
                            type="checkbox"
                            checked={column.getIsVisible()}
                            onChange={column.getToggleVisibilityHandler()}
                            className="mr-3 h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            {typeof column.columnDef.header === "string" ? column.columnDef.header : column.id}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <Button
              variant="secondary-outline"
              onClick={handleExportCsv}
              rounded="rounded-lg"
              padding="px-2 py-2"
              className="text-sm"
            >
              <i className="fa-solid fa-download mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        <div className="flex justify-between items-center mb-2 px-1 text-xs text-gray-400">
          <span>{total === 0 ? "No plans" : `Showing ${from}–${to} of ${total}`}</span>
        </div>

        <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
          <div className="overflow-auto flex-1 min-h-0">
            <table
              className="border-separate border-spacing-0"
              style={{ width: table.getTotalSize(), minWidth: "900px", tableLayout: "fixed" }}
            >
              <thead>
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id} className="bg-gray-50">
                    {hg.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          width: header.getSize(),
                          ...(header.id === "name" ? { left: 0, zIndex: 20 } : {}),
                        }}
                        className={`group relative sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400 select-none whitespace-nowrap border-b border-gray-100${header.id === "name" ? " border-r border-gray-200" : ""}`}
                      >
                        {header.isPlaceholder ? null : (
                          <>
                            <div
                              className={header.column.getCanSort() ? "flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors" : ""}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getCanSort() && (
                                <span className="text-gray-300">
                                  {{ asc: "↑", desc: "↓" }[header.column.getIsSorted()] ?? "↕"}
                                </span>
                              )}
                            </div>
                            {header.column.getCanResize() && (
                              <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onClick={e => e.stopPropagation()}
                                className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none ${header.column.getIsResizing() ? "bg-brand opacity-100" : "bg-gray-300 opacity-0 group-hover:opacity-100"}`}
                              />
                            )}
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={table.getVisibleLeafColumns().length} className="py-16 text-center text-gray-400 text-sm">
                      <i className="fa fa-spinner fa-spin mr-2" />Loading…
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={table.getVisibleLeafColumns().length} className="py-16 text-center">
                      <i className="fa-regular fa-folder-open text-3xl text-gray-200 mb-3 block" />
                      <p className="font-semibold text-gray-500 text-sm">No plans found</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting the search or filters above.</p>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={`group/row border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          title={cell.getValue() != null ? String(cell.getValue()) : undefined}
                          style={{
                            width: cell.column.getSize(),
                            ...(cell.column.id === "name" ? { position: "sticky", left: 0, zIndex: 1 } : {}),
                          }}
                          className={`truncate px-3 py-2 align-middle text-sm${cell.column.id === "name" ? ` border-r border-gray-200 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"} group-hover/row:bg-gray-50` : ""}`}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="flex items-center justify-between mt-4 px-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-sm text-gray-600">
                Page {pageIndex + 1} of {pageCount}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
            <select
              value={pageSize}
              onChange={e => table.setPageSize(Number(e.target.value))}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 outline-none focus:ring-2 focus:ring-brand/30"
            >
              {[10, 25, 50, 100].map(s => (
                <option key={s} value={s}>{s} / page</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedPlan && (
        <Modal isOpen={editPlanOpen} setIsOpen={setEditPlanOpen} width="md:max-w-xl">
          <EditPlan
            plan={selectedPlan}
            onEdit={() => { setEditPlanOpen(false); refreshPlans() }}
            onDelete={() => { setEditPlanOpen(false); refreshPlans() }}
          />
        </Modal>
      )}

      <ArchivePlanModal
        isOpen={archivePlanOpen}
        onClose={() => setArchivePlanOpen(false)}
        onSuccess={refreshPlans}
        plan={selectedPlan}
      />

      <ChangePlanOrganizationModal
        isOpen={changeOrgOpen}
        onClose={() => setChangeOrgOpen(false)}
        onSuccess={refreshPlans}
        plan={selectedPlan}
      />

      <Modal isOpen={inviteMentorsOpen} setIsOpen={setInviteMentorsOpen} width="md:max-w-lg">
        <div className="p-0">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Invite Mentors</h2>
          <p className="text-gray-600 mb-4 text-sm">
            An invitation email will be sent to all of the recipients below. Uncheck a box to not send an invitation email.
          </p>
          {inviteMentorsListLoading ? (
            <div className="text-gray-500 text-sm py-6 text-center">Loading mentors...</div>
          ) : inviteMentorsList.length === 0 && !inviteMentorsError ? (
            <div className="text-gray-400 italic text-sm mb-4">No mentors found for this plan.</div>
          ) : (
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {inviteMentorsList.map(mentor => {
                const isChecked = selectedMentorEmails.has(mentor.email)
                return (
                  <label
                    key={mentor.email}
                    className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${isChecked ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleMentorEmail(mentor.email)}
                      className="h-4 w-4 shrink-0 accent-brand"
                    />
                    <InvitationStatusBadge status={mentor.invitation_status} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">{mentor.first_name} {mentor.last_name}</div>
                      <div className="text-xs text-gray-500 truncate">{mentor.email}</div>
                    </div>
                  </label>
                )
              })}
            </div>
          )}
          {inviteMentorsError && <p className="text-red-600 text-sm mb-4">{inviteMentorsError}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="secondary-outline" onClick={() => { setInviteMentorsOpen(false); setInviteMentorsError(null) }} disabled={invitingMentors}>
              Cancel
            </Button>
            <Button onClick={handleInviteMentorsAction} disabled={invitingMentors || inviteMentorsListLoading || selectedMentorEmails.size === 0}>
              {invitingMentors ? <><i className="fa fa-spinner fa-spin mr-2" />Sending…</> : "Send Invitations"}
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  )
}

export default AdminPlans
